/**
 * File Service - Manages project files and folder navigation
 * Provides API calls for fetching files associated with projects
 */

import { API_BASE_URL } from '../API/api';
import axios from 'axios';
import { getDownloadsContext } from '../contexts/DownloadsContext';

class FileService {
  constructor() {
    console.log('📁 FileService initialized');
    this.usersCache = new Map(); // Cache for user data
  }

  /**
   * Fetch all users from the database
   * @returns {Promise<Map>} Map of user ID to user data
   */
  async getUsers() {
    console.log('👥 getUsers called');
    
    // Return cached data if available
    if (this.usersCache.size > 0) {
      console.log('👥 Returning cached users:', this.usersCache.size, 'users');
      return this.usersCache;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/users`);
      console.log('👥 Users API Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('👥 Users API Response data:', data);
      
      if (data.users) {
        // Cache the users data
        data.users.forEach(user => {
          this.usersCache.set(user.id, {
            id: user.id,
            email: user.email,
            fname: user.fname,
            lname: user.lname,
            fullName: `${user.fname || ''} ${user.lname || ''}`.trim()
          });
        });
        
        console.log('✅ Cached users:', this.usersCache.size, 'users');
        return this.usersCache;
      } else {
        console.warn('⚠️ No users in response data');
        return new Map();
      }
    } catch (error) {
      console.error('❌ Error in getUsers:', error);
      return new Map();
    }
  }

  /**
   * Get user name by ID
   * @param {number} userId - The user ID
   * @returns {string} Full name or 'Unknown' if not found
   */
  getUserName(userId) {
    const user = this.usersCache.get(userId);
    return user ? user.fullName : 'Unknown';
  }

  /**
   * Fetch all files for a specific project
   * @param {number} projectId - The 4-digit project ID
   * @returns {Promise<Array>} Array of file objects
   */
  async getProjectFiles(projectId) {
    console.log('📁 getProjectFiles called for project:', projectId);
    
    try {
      // First, fetch users to get owner names
      await this.getUsers();
      
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/files`);
      console.log('📁 API Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📁 API Response data:', data);
      
      if (data.files) {
        const formattedFiles = data.files.map(file => this.formatFileForFrontend(file));
        console.log('✅ Formatted files:', formattedFiles.length, 'items');
        return formattedFiles;
      } else {
        console.warn('⚠️ No files in response data');
        return [];
      }
    } catch (error) {
      console.error('❌ Error in getProjectFiles:', error);
      throw error;
    }
  }

  /**
   * Convert file data from database format to frontend format
   * @param {Object} dbFile - File data from database
   * @returns {Object} Formatted file object for frontend
   */
  formatFileForFrontend(dbFile) {
    console.log('🔧 formatFileForFrontend called with:', dbFile);
    
    const formatted = {
      id: dbFile.id,
      name: dbFile.name,
      isFolder: dbFile.isfolder === 1,
      size: dbFile.size || 0,
      projectId: dbFile.projects_id,
      parentId: dbFile.parent_id,
      ownerId: dbFile.owner_id,
      ownerName: this.getUserName(dbFile.owner_id), // Get full name from users cache
      isDeleted: dbFile.isdeleted === 1,
      modified: dbFile.modified ? new Date(dbFile.modified) : new Date(),
      created: dbFile.created ? new Date(dbFile.created) : new Date(),
      // Additional computed properties
      isRoot: dbFile.parent_id === dbFile.projects_id,
      hasChildren: false, // Will be computed when building tree
      children: [], // Will be populated when building tree
      level: 0, // Will be computed when building tree
      expanded: false // For UI state
    };
    
    console.log('🔧 Formatted file:', formatted);
    return formatted;
  }

  /**
   * Build a hierarchical tree structure from flat file list
   * @param {Array} files - Array of formatted file objects
   * @param {number} projectId - The project ID
   * @returns {Array} Tree structure with root folders
   */
  buildFileTree(files, projectId) {
    console.log('🌳 buildFileTree called with:', files.length, 'files for project:', projectId);
    
    // Filter out deleted files and files not belonging to this project
    const validFiles = files.filter(file => 
      !file.isDeleted && 
      file.projectId === projectId
    );
    
    console.log('🌳 Valid files after filtering:', validFiles.length);
    
    // Create a map for quick lookup
    const fileMap = new Map();
    validFiles.forEach(file => {
      fileMap.set(file.id, { ...file, children: [], hasChildren: false, level: 0, expanded: false });
    });
    
    // Build the tree structure
    const rootFiles = [];
    
    validFiles.forEach(file => {
      const fileNode = fileMap.get(file.id);
      
      if (file.isRoot) {
        // This is a root-level file/folder
        fileNode.level = 0;
        rootFiles.push(fileNode);
      } else {
        // This file has a parent
        const parent = fileMap.get(file.parentId);
        if (parent) {
          parent.children.push(fileNode);
          parent.hasChildren = true;
          fileNode.level = parent.level + 1;
        } else {
          // Parent not found, treat as root
          console.warn('⚠️ Parent not found for file:', file.name, 'parentId:', file.parentId);
          fileNode.level = 0;
          rootFiles.push(fileNode);
        }
      }
    });
    
    // Sort folders first, then files, alphabetically
    const sortFiles = (files) => {
      return files.sort((a, b) => {
        if (a.isFolder && !b.isFolder) return -1;
        if (!a.isFolder && b.isFolder) return 1;
        return a.name.localeCompare(b.name);
      });
    };
    
    // Recursively sort all levels
    const sortTree = (files) => {
      files.forEach(file => {
        if (file.children.length > 0) {
          file.children = sortFiles(sortTree(file.children));
        }
      });
      return sortFiles(files);
    };
    
    const sortedTree = sortTree(rootFiles);
    console.log('🌳 Built file tree with', sortedTree.length, 'root items');
    
    return sortedTree;
  }

  /**
   * Find a file by ID in the tree structure
   * @param {Array} tree - The file tree
   * @param {number} fileId - The file ID to find
   * @returns {Object|null} The file object or null if not found
   */
  findFileInTree(tree, fileId) {
    for (const file of tree) {
      if (file.id === fileId) {
        return file;
      }
      if (file.children.length > 0) {
        const found = this.findFileInTree(file.children, fileId);
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * Get the path to a file in the tree
   * @param {Array} tree - The file tree
   * @param {number} fileId - The file ID to find
   * @returns {Array} Array of file objects representing the path
   */
  getFilePath(tree, fileId) {
    const path = [];
    
    const findPath = (files, targetId, currentPath) => {
      for (const file of files) {
        const newPath = [...currentPath, file];
        if (file.id === targetId) {
          return newPath;
        }
        if (file.children.length > 0) {
          const found = findPath(file.children, targetId, newPath);
          if (found) return found;
        }
      }
      return null;
    };
    
    return findPath(tree, fileId, []) || [];
  }

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  formatFileSize(bytes) {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get file icon based on file type
   * @param {Object} file - File object
   * @returns {string} FontAwesome icon class
   */
  getFileIcon(file) {
    if (file.isFolder) {
      return 'fas fa-folder';
    }
    
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return 'fas fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fas fa-file-word';
      case 'xls':
      case 'xlsx':
        return 'fas fa-file-excel';
      case 'ppt':
      case 'pptx':
        return 'fas fa-file-powerpoint';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return 'fas fa-file-image';
      case 'mp4':
      case 'avi':
      case 'mov':
        return 'fas fa-file-video';
      case 'mp3':
      case 'wav':
        return 'fas fa-file-audio';
      case 'zip':
      case 'rar':
      case '7z':
        return 'fas fa-file-archive';
      case 'txt':
        return 'fas fa-file-alt';
      case 'json':
        return 'fas fa-file-code';
      case 'js':
      case 'jsx':
      case 'ts':
      case 'tsx':
        return 'fas fa-file-code';
      case 'css':
        return 'fas fa-file-code';
      case 'html':
        return 'fas fa-file-code';
      case 'py':
        return 'fas fa-file-code';
      case 'java':
        return 'fas fa-file-code';
      case 'cpp':
      case 'c':
        return 'fas fa-file-code';
      default:
        return 'fas fa-file';
    }
  }

  /**
   * Download a single file by ID
   * @param {number} fileId - The file ID
   * @param {string} fileName - The file name for the download
   * @param {number} projectId - The project ID (optional but recommended for unique storage)
   * @param {Function} progressCallback - Callback function (progress, status)
   * @param {Object} cancelTokenSource - Axios cancel token source for cancellation
   * @returns {Promise<void>}
   */
  async downloadFile(fileId, fileName, projectId = null, progressCallback = null, cancelTokenSource = null) {
    console.log('📥 downloadFile called for file:', fileId, fileName, 'project:', projectId);
    
    // Build URL with project_id query parameter if provided
    let downloadUrl = `${API_BASE_URL}/api/files/${fileId}/download`;
    if (projectId) {
      downloadUrl += `?project_id=${projectId}`;
    }
    
    // Trigger immediate download using direct link - browser handles streaming natively
    // This allows the browser to start downloading immediately without waiting
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    console.log('🚀 Download triggered immediately - browser handling streaming');
    
    // Clean up link immediately
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
    
    // Track progress separately using fetch (runs in parallel, doesn't block download)
    // Note: This creates a second request, but it's necessary for progress tracking
    // The browser's native download (from link.click above) will be faster
    if (progressCallback) {
      try {
        const controller = new AbortController();
        
        // Handle cancellation - if axios cancel token is provided, we can't easily convert it
        // but we can still abort the progress tracking fetch
        // Note: The browser's native download (from link.click) cannot be cancelled
        // This only cancels the progress tracking request
        
        const response = await fetch(downloadUrl, {
          signal: controller.signal,
          headers: { 'Accept': '*/*' }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const contentLength = response.headers.get('content-length');
        const total = contentLength ? parseInt(contentLength, 10) : null;
        
        const reader = response.body.getReader();
        let loaded = 0;
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          loaded += value.length;
          
          if (total) {
            const percentCompleted = Math.round((loaded * 100) / total);
            progressCallback(percentCompleted, 'downloading');
          } else if (loaded > 0) {
            // Total size unknown, estimate progress
            progressCallback(50, 'downloading');
          }
        }
        
        progressCallback(100, 'downloading');
        console.log('✅ Progress tracking completed');
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('📥 Progress tracking cancelled');
        } else {
          console.warn('⚠️ Progress tracking failed (download may still succeed):', error);
        }
        // Don't throw - download may still succeed even if progress tracking fails
      }
    }
  }

  /**
   * Download multiple files as a ZIP archive
   * @param {Array} fileIds - Array of file IDs to download
   * @param {string} zipFileName - Name for the ZIP file
   * @param {Function} progressCallback - Callback function (progress, status)
   * @param {Object} cancelTokenSource - Axios cancel token source for cancellation
   * @param {boolean} skipTracking - If true, don't track this download in DownloadsContext (for re-downloads)
   * @returns {Promise<void>}
   */
  async downloadFilesAsZip(fileIds, zipFileName = 'files.zip', progressCallback = null, cancelTokenSource = null, skipTracking = false) {
    console.log('📦 downloadFilesAsZip called for', fileIds.length, 'files', skipTracking ? '(skip tracking)' : '');
    
    // Track download in DownloadsContext (unless skipTracking is true)
    const downloadsContext = getDownloadsContext();
    let downloadId = null;
    
    // Create cancel token if not provided
    let internalCancelToken = cancelTokenSource;
    if (!internalCancelToken) {
      const CancelToken = axios.CancelToken;
      internalCancelToken = CancelToken.source();
    }
    
    if (downloadsContext && !skipTracking) {
      downloadId = downloadsContext.addDownload({
        fileName: zipFileName,
        fileIds: fileIds,
        status: 'creating',
        progress: 0,
        cancelTokenSource: internalCancelToken,
      });
    }
    
    try {
      // Notify that ZIP creation is starting
      if (progressCallback) {
        progressCallback(5, 'creating');
      }
      
      if (downloadsContext && downloadId && !skipTracking) {
        downloadsContext.updateDownload(downloadId, { progress: 5, status: 'creating' });
      }
      
      const response = await axios({
        url: `${API_BASE_URL}/api/files/download-zip`,
        method: 'POST',
        data: { file_ids: fileIds },
        responseType: 'blob',
        timeout: 600000, // 10 minute timeout for large ZIPs
        cancelToken: internalCancelToken ? internalCancelToken.token : undefined,
        headers: {
          'Content-Type': 'application/json',
        },
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`📦 ZIP download progress: ${percentCompleted}%`);
            
            // When download starts, we know ZIP creation is done
            if (progressCallback && percentCompleted > 0) {
              // ZIP creation is done (85%), now downloading (85-100%)
              const totalProgress = 85 + Math.round(percentCompleted * 0.15);
              progressCallback(Math.min(totalProgress, 100), 'downloading');
            }
            
            // Update downloads context
            if (downloadsContext && downloadId && !skipTracking) {
              const totalProgress = 85 + Math.round(percentCompleted * 0.15);
              downloadsContext.updateDownload(downloadId, {
                progress: Math.min(totalProgress, 100),
                status: 'downloading',
              });
            }
          } else if (progressEvent.loaded > 0) {
            // Download has started but we don't know total size yet
            // ZIP creation is done, now downloading
            if (progressCallback) {
              progressCallback(85, 'downloading');
            }
            
            if (downloadsContext && downloadId && !skipTracking) {
              downloadsContext.updateDownload(downloadId, { progress: 85, status: 'downloading' });
            }
          }
        }
      });

      // Check if response has data
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      // Create blob and trigger download
      const blob = new Blob([response.data], { type: 'application/zip' });
      console.log('📦 Blob created, size:', blob.size, 'bytes', skipTracking ? '(skip tracking)' : '');
      
      if (blob.size === 0) {
        throw new Error('Received empty ZIP file from server');
      }
      
      // Store blob in downloads context for re-download (only if tracking)
      if (downloadsContext && downloadId && !skipTracking) {
        downloadsContext.updateDownload(downloadId, {
          status: 'completed',
          progress: 100,
          blob: blob,
        });
      }
      
      // Always trigger download, regardless of skipTracking
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = zipFileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      console.log('🚀 Triggering download for:', zipFileName, 'Blob size:', blob.size, 'bytes');
      
      // Use a small delay to ensure the link is in the DOM
      requestAnimationFrame(() => {
        link.click();
        console.log('✅ Download link clicked');
        
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          console.log('✅ Cleanup completed');
        }, 100);
      });
      
      console.log('✅ ZIP file downloaded successfully:', zipFileName);
    } catch (error) {
      // Check if request was cancelled
      if (axios.isCancel(error)) {
        console.log('📦 ZIP download cancelled by user');
        
        // Update downloads context (only if tracking)
        if (downloadsContext && downloadId && !skipTracking) {
          downloadsContext.removeDownload(downloadId);
        }
        
        throw error; // Re-throw cancellation error without modification
      }
      console.error('❌ Error downloading ZIP file:', error);
      
      // Update downloads context with error (only if tracking)
      if (downloadsContext && downloadId && !skipTracking) {
        downloadsContext.updateDownload(downloadId, {
          status: 'error',
        });
      }
      
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`Failed to download ZIP: ${errorMessage}`);
    }
  }

  /**
   * Download multiple files
   * Best practice: Use ZIP for multiple files (2+), individual download for single file
   * @param {Array} files - Array of file objects to download
   * @param {Function} progressCallback - Callback function (progress, status)
   * @param {Object} cancelTokenSource - Axios cancel token source for cancellation
   * @returns {Promise<void>}
   */
  async downloadFiles(files, progressCallback = null, cancelTokenSource = null) {
    console.log('📥 downloadFiles called for', files.length, 'files');
    
    // Filter out folders - only download actual files
    const filesToDownload = files.filter(file => !file.isFolder);
    
    if (filesToDownload.length === 0) {
      console.warn('⚠️ No files to download (only folders selected)');
      return;
    }

    // Best practice: Use ZIP for multiple files, individual download for single file
    if (filesToDownload.length === 1) {
      // Single file - download directly
      console.log('📥 Downloading single file...');
      if (progressCallback) {
        progressCallback(0, 'downloading');
      }
      // Pass projectId from file object to ensure correct table/bucket lookup
      await this.downloadFile(filesToDownload[0].id, filesToDownload[0].name, filesToDownload[0].projectId, progressCallback, cancelTokenSource);
      if (progressCallback) {
        progressCallback(100, 'downloading');
      }
    } else {
      // Multiple files - create ZIP
      console.log(`📦 Downloading ${filesToDownload.length} files as ZIP...`);
      const fileIds = filesToDownload.map(file => file.id);
      const zipFileName = `files_${filesToDownload.length}_items.zip`;
      await this.downloadFilesAsZip(fileIds, zipFileName, progressCallback, cancelTokenSource);
      if (progressCallback) {
        progressCallback(100, 'downloading');
      }
    }

    console.log('✅ All files download process completed');
  }

  /**
   * Create a new folder in a project
   * @param {number} projectId - The project ID
   * @param {string} folderName - The name of the folder to create
   * @param {number|null} parentId - The parent folder ID (null for root, defaults to projectId)
   * @returns {Promise<Object>} The created folder object
   */
  async createFolder(projectId, folderName, parentId = null) {
    console.log('📁 createFolder called for project:', projectId, 'name:', folderName, 'parent:', parentId);
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/projects/${projectId}/folders`,
        {
          name: folderName,
          parent_id: parentId || projectId
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('✅ Folder created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating folder:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`Failed to create folder: ${errorMessage}`);
    }
  }

  /**
   * Soft delete files/folders (sets isdeleted=1 in MySQL)
   * @param {Array<number>} fileIds - Array of file IDs to delete
   * @returns {Promise<Object>} The delete response
   */
  async deleteFiles(fileIds) {
    console.log('🗑️ deleteFiles called for:', fileIds);
    
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/files/delete`, {
        data: {
          file_ids: fileIds
        },
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Files deleted successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting files:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`Failed to delete files: ${errorMessage}`);
    }
  }
}

// Create and export a singleton instance
const fileService = new FileService();
export default fileService;

